import type { PixelEvent } from '@/events';

export type BackpressurePolicy =
  | 'drop_oldest'
  | 'drop_newest'
  | 'reject_new'
  | 'sample'
  | 'none';

export type BackpressureConfig = {
  readonly enabled: boolean;
  readonly maxQueueSize: number;
  readonly policy: BackpressurePolicy;
  readonly sampleRate?: number;
};

export type BackpressureDecision = {
  readonly accepted: boolean;
  readonly dropped: number;
  readonly policy: BackpressurePolicy;
  readonly reason?: string;
};

export type BackpressureDebugState = {
  readonly enabled: boolean;
  readonly currentSize: number;
  readonly maxQueueSize: number;
  readonly policy: BackpressurePolicy;
  readonly totalDropped: number;
  readonly totalRejected: number;
  readonly totalSampled: number;
};

export class BackpressureManager {
  private readonly config: BackpressureConfig;

  private totalDropped = 0;

  private totalRejected = 0;

  private totalSampled = 0;

  constructor(config: BackpressureConfig) {
    this.config = config;
  }

  evaluateEnqueue(currentSize: number): BackpressureDecision {
    if (!this.config.enabled || this.config.policy === 'none') {
      return { accepted: true, dropped: 0, policy: this.config.policy };
    }

    if (currentSize < this.config.maxQueueSize) {
      return { accepted: true, dropped: 0, policy: this.config.policy };
    }

    switch (this.config.policy) {
      case 'reject_new':
        this.totalRejected += 1;
        return {
          accepted: false,
          dropped: 0,
          policy: this.config.policy,
          reason: 'queue_full_reject_new',
        };
      case 'sample': {
        const rate = this.config.sampleRate ?? 0.5;
        const accept = Math.random() < rate;
        if (!accept) this.totalSampled += 1;
        return {
          accepted: accept,
          dropped: accept ? 0 : 1,
          policy: this.config.policy,
          reason: accept ? undefined : 'sampled_out',
        };
      }
      case 'drop_oldest':
      case 'drop_newest':
        return { accepted: true, dropped: 1, policy: this.config.policy };
      default:
        return { accepted: true, dropped: 0, policy: 'none' };
    }
  }

  applyPolicy<T>(items: T[], incoming: T): { items: T[]; dropped: number } {
    const decision = this.evaluateEnqueue(items.length);

    if (!decision.accepted) {
      return { items, dropped: decision.dropped };
    }

    if (items.length < this.config.maxQueueSize) {
      return { items: [...items, incoming], dropped: 0 };
    }

    if (this.config.policy === 'drop_oldest') {
      this.totalDropped += 1;
      return { items: [...items.slice(1), incoming], dropped: 1 };
    }

    if (this.config.policy === 'drop_newest') {
      this.totalDropped += 1;
      return { items, dropped: 1 };
    }

    return { items: [...items, incoming], dropped: 0 };
  }

  getDebugState(currentSize: number): BackpressureDebugState {
    return {
      enabled: this.config.enabled,
      currentSize,
      maxQueueSize: this.config.maxQueueSize,
      policy: this.config.policy,
      totalDropped: this.totalDropped,
      totalRejected: this.totalRejected,
      totalSampled: this.totalSampled,
    };
  }
}

export type { PixelEvent };
